import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Paper,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Email,
  Phone,
  CheckCircleOutline,
  Apartment,
  LocationOn,
  Numbers,
  DateRange,
  MonetizationOn,
  Warning,
  Search,
  StairsRounded,
  Home,
  HolidayVillage,
  LocationCity,
} from "@mui/icons-material";
import stringToColor from "../services/stringToColor";
import axios from "axios";
import { useUser } from "../provider/AuthProvider";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const AssignPropertyDialog = ({ open, onClose, tenant }) => {
  const { i18n } = useTranslation();
  const [availableProperty, setAvailableProperty] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [amount, setAmount] = useState("");
  const { user } = useUser();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  useEffect(() => {
    if (open) {
      fetchProperty();
      setSelectedProperty(null); // Optional: reset selection on open
      setSearchTerm("");
    }
  }, [open]);

  const fetchProperty = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/availableProperties/${user.id}`
      );
      console.log(res.data);
      setAvailableProperty(res.data);
    } catch (error) {
      console.error("Failed to fetch service types:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const filtered = [];
    availableProperty.forEach((building) => {
      const units = building.units.filter((unit) =>
        unit.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (units.length > 0) {
        filtered.push({ ...building, units });
      }
    });
    setFilteredUnits(filtered);
  }, [searchTerm, availableProperty]);

  const handleSelectProperty = (property) => {
    setSelectedProperty(property);
  };

  const handleConfirm = async () => {
    if (!selectedProperty || !tenant) {
      setSnack({
        open: true,
        message: "Please select a property and tenant.",
        severity: "error",
      });
      return;
    }
    // if (!startDate || !endDate || !amount) {
    //   setSnack({
    //     open: true,
    //     message: "Please input the startDate, endDate and amount.",
    //     severity: "error",
    //   });
    //   return;
    // }
    // if (new Date(startDate) >= new Date(endDate)) {
    //   setSnack({
    //     open: true,
    //     message:
    //       "Please enter an exact date. The end date must be after the start date.",
    //     severity: "error",
    //   });
    //   return;
    // }
    // if (parseFloat(amount) <= 0) {
    //   setSnack({
    //     open: true,
    //     message: "Please enter an exact amount.",
    //     severity: "error",
    //   });
    //   return;
    // }
    setIsSubmit(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/assignProperty`,
        {
          cmt_id: user.id,
          tenant_id: tenant.id,
          unit_id: selectedProperty.id,
          start_date: startDate,
          end_date: endDate,
          amount: parseFloat(amount),
        }
      );

      setSnack({
        open: true,
        message: "Property assigned successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to assign property:", err);
      setSnack({
        open: true,
        message: "Failed to assign property. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmit(false);
      onClose(); // Close dialog
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h3" fontWeight="bold">
          Assign Unit to Tenant
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} columns={{ xs: 2, sm: 6, md: 8 }}>
          <Grid item size={{ xs: 2, sm: 2, md: 3 }}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={2}
                className="notranslate"
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    mb: 2,
                    bgcolor: stringToColor(tenant?.username),
                    fontSize: 32,
                  }}
                  className="notranslate"
                >
                  {tenant?.username?.[0]?.toUpperCase() || "?"}
                </Avatar>
                <Typography
                  variant="h6"
                  textAlign="center"
                  className="notranslate"
                >
                  {tenant?.username}
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                className="notranslate"
              >
                <Email fontSize="small" color="action" />
                <Typography variant="body2">{tenant?.email}</Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
                className="notranslate"
              >
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" dir="ltr">
                  {tenant?.phone}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {selectedProperty ? (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {selectedProperty.type.toLowerCase() == "tower" ? (
                      <LocationCity fontSize="small" color="action" />
                    ) : (
                      <HolidayVillage fontSize="small" color="action" />
                    )}
                    <Typography variant="body2" className="notranslate">
                      {selectedProperty.building}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1}
                    className="notranslate"
                  >
                    <Apartment fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedProperty.name}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1}
                    className="notranslate"
                  >
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedProperty.address}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Numbers fontSize="small" color="action" />
                    {selectedProperty.type.toLowerCase() == "tower" ? (
                      <Typography variant="body2">
                        Floor {selectedProperty.floor}, &nbsp; Flat{" "}
                        {selectedProperty.flat_number}
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        Block {selectedProperty.floor}, &nbsp; Villa{" "}
                        {selectedProperty.flat_number}
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2, mt: 2 }} />
                  <Box>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                    >
                      <DatePicker
                        label={
                          i18n.language === "ar" ? "تاريخ البدء" : "Start Date"
                        }
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(newValue) =>
                          setStartDate(
                            newValue ? newValue.format("YYYY-MM-DD") : ""
                          )
                        }
                        slotProps={{
                          textField: {
                            placeholder: "", // ✅ Hide placeholder
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
                    >
                      <DatePicker
                        label={
                          i18n.language === "ar" ? "تاريخ الانتهاء" : "End Date"
                        }
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newValue) =>
                          setEndDate(
                            newValue ? newValue.format("YYYY-MM-DD") : ""
                          )
                        }
                        slotProps={{
                          textField: {
                            placeholder: "", // ✅ Hide placeholder
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {/* <TextField
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateRange />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateRange />
                          </InputAdornment>
                        ),
                      }}
                    /> */}
                    <TextField
                      label="Amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      margin="normal"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MonetizationOn />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No unit selected.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Property List Section */}
          <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
              <Box mb={2} display="flex" alignItems="center">
                <Search sx={{ mr: 1 }} />
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by unit number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
              <Paper
                elevation={3}
                sx={{
                  maxHeight: 500,
                  overflowY: "auto",
                  p: 5,
                  borderRadius: 3,
                }}
              >
                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : filteredUnits.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={5}
                  >
                    <Warning sx={{ fontSize: 64, mb: 2 }} color="warning" />
                    <Typography variant="h6" color="warning" textAlign="center">
                      There are no available units.
                      <br />
                      Please add new units or check again later.
                    </Typography>
                  </Box>
                ) : (
                  filteredUnits.map((building) => (
                    <Box key={building.building_id} mb={2}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        className="notranslate"
                      >
                        {building.building_name} - {building.location}
                      </Typography>

                      {building.units.length === 0 ? (
                        <Typography variant="body2" color="warning" mb={1}>
                          There are no available units in this building.
                        </Typography>
                      ) : (
                        <List dense>
                          {building.units.map((unit) => {
                            let buildingType = building.type.toLowerCase();
                            return (
                              <ListItem
                                key={unit.id}
                                disablePadding
                                sx={{
                                  backgroundColor: "rgba(0,0,0,0.1)",
                                  mt: 1,
                                }}
                              >
                                <ListItemButton
                                  selected={selectedProperty?.id === unit.id}
                                  onClick={() =>
                                    handleSelectProperty({
                                      ...unit,
                                      building: building.building_name,
                                      address: building.location,
                                      type: building.type,
                                    })
                                  }
                                >
                                  <ListItemText
                                    primary={
                                      <React.Fragment>
                                        <Typography className="notranslate">
                                          {unit.name}
                                        </Typography>
                                      </React.Fragment>
                                    }
                                    secondary={
                                      <React.Fragment>
                                        <Box display="flex" gap={15}>
                                          <Box display="flex" gap={5}>
                                            <Box display="flex" gap={2}>
                                              {buildingType == "tower" ? (
                                                <Typography>
                                                  Floor : {unit.floor}
                                                </Typography>
                                              ) : (
                                                <Typography>
                                                  Block : {unit.floor}
                                                </Typography>
                                              )}
                                            </Box>
                                            <Box display="flex" gap={2}>
                                              {buildingType == "tower" ? (
                                                <Typography>
                                                  Flat : {unit.flat_number}
                                                </Typography>
                                              ) : (
                                                <Typography>
                                                  Villa : {unit.flat_number}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                          <Chip
                                            label={`${unit.status}`}
                                            size="small"
                                            color={
                                              unit.status.toLowerCase() ==
                                              "vacant"
                                                ? "error"
                                                : "success"
                                            }
                                          ></Chip>
                                        </Box>
                                      </React.Fragment>
                                    }
                                  />
                                  {selectedProperty?.id === unit.id && (
                                    <CheckCircleOutline color="success" />
                                  )}
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      )}
                    </Box>
                  ))
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedProperty || isSubmit}
        >
          {isSubmit ? "Confirm..." : "Confirm"}
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AssignPropertyDialog;
